import { ActionRequiredBanner } from "@/common/components/actionRequiredBanner";
import { executeCaptchaV2, resetCaptcha } from "@/common/components/recaptcha/Recaptcha.component";
import { RecaptchaBlurb } from "@/common/components/recaptcha/RecaptchaBlurb.component";
import { trackPage } from "@/common/utilities/analyticsUtility";
import { Banner, Button, CircularProgress, TextInput, Typography } from "@chr/eds-react"
import { useTranslation } from "@chr/online-shared-ui-localizations"
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack"
import { useEffect, useState, useCallback } from "react";
import { useSelector, useDispatch } from 'react-redux';
import { AppState } from '../../app-state';
import { submitGenerateRotpToken, resetLoadedInvoices } from '../../common/actions/startPageActions';
import { LocalConfig } from '../../common/constants/localConfig';
import GuestUserRedirect from "@/common/components/guestUserRedirect/GuestUserRedirect.component";

interface StartPageProps {
  config: LocalConfig;
}

export const StartPage = ({ config }: StartPageProps) => {
    const translate = useTranslation();
    const dispatch = useDispatch();

    // Redux state from mapStateToProps
    const loadInvoiceError = useSelector((state: AppState) => state.error.loadInvoiceError);
    const loadInvoiceFailure = useSelector((state: AppState) => state.error.loadInvoiceFailure);
    const reCaptchaFailure = useSelector((state: AppState) => state.error.reCaptchaFailure);
    const recaptchaV2Token = useSelector((state: AppState) => state.recaptcha.v2Token);

    const [invoiceNumber, setInvoiceNumber] = useState('');
    const [amount, setAmount] = useState('');
    const [loadNumber, setLoadNumber] = useState('');
    const [invoiceSearchLoading, setInvoiceSearchLoading] = useState(false);

    // Redux actions from mapDispatchToProps
    const dispatchSubmitGenerateRotpToken = useCallback((invoiceNum: number, invoiceAmt: number, loadNum: number, token: string, recaptchaType: string) =>
        dispatch(submitGenerateRotpToken(invoiceNum, invoiceAmt, loadNum, token, recaptchaType)), [dispatch]);

    const dispatchResetLoadedInvoices = useCallback(() => dispatch(resetLoadedInvoices()), [dispatch]);

    const handleSubmitInvoiceSearch = async (e: React.FormEvent) => {
        e.preventDefault();
        const token = ''; // Ignore recaptcha v3 token for now

        dispatchResetLoadedInvoices();
        submitInvoiceSearchAction(token, "reCaptchaV3"); // Need to keep this as v3 until the api gets changed
    };

    const submitInvoiceSearchAction = useCallback((token: string, recaptchaType: string) => {
        if (invoiceNumber.length) {
            setInvoiceSearchLoading(true);

            // Always use guest payment processor (previously controlled by LaunchDarkly flag)
            dispatchSubmitGenerateRotpToken(
                parseInt(invoiceNumber),
                parseFloat(amount),
                parseInt(loadNumber),
                token,
                recaptchaType,
            );
        }
    }, [invoiceNumber, amount, loadNumber, dispatchSubmitGenerateRotpToken]);

    // Handle amount input validation (decimal input)
    const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const decimal = /^\d*(\.\d{0,2})?$/;
        const input = e.target.value;

        if (input.match(decimal)) {
            setAmount(input);
        }
    };

    useEffect(() => {
        trackPage('Payment Start Page');
        resetCaptcha();
    }, []);

    // Handle recaptcha failures and token updates (from componentDidUpdate)
    useEffect(() => {
        const handleRecaptchaFailure = async () => {
            resetCaptcha();
            await executeCaptchaV2();
        };

        if (reCaptchaFailure !== null) {
            handleRecaptchaFailure();
        }
    }, [reCaptchaFailure]);

    useEffect(() => {
        if (recaptchaV2Token != null) {
            submitInvoiceSearchAction(recaptchaV2Token, "reCaptchaV2");
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [recaptchaV2Token])

    return (
        <Stack gap={5}>
            <Box>
                <Typography id="paymentsTitle" variant="h1">{translate("Pay Invoice")}</Typography>
                <Typography variant="h2">{translate("Pay securely and easily online using your U.S. bank account or credit card.")}</Typography>
            </Box>
            <ActionRequiredBanner />
            <Stack gap={3}>
                <Typography variant="h2" fontWeight="semiBold">{translate("Search your invoice")}</Typography>
                <form onSubmit={handleSubmitInvoiceSearch}>
                    <Stack direction={'row'} gap={4}>
                        <TextInput
                            label={translate("Invoice number")}
                            value={invoiceNumber}
                            onChange={(e) => setInvoiceNumber(e.target.value)}
                            required
                        />
                        <TextInput
                            label={translate("Amount")}
                            value={amount}
                            onChange={handleAmountChange}
                            inputType="number"
                            required
                        />
                        <TextInput
                            label={translate("Load/Order number")}
                            value={loadNumber}
                            onChange={(e) => setLoadNumber(e.target.value)}
                            required
                        />
                    </Stack>
                    <RecaptchaBlurb />
                    <Box mt={3} sx={{ width: 'fit-content' }}>
                        <Button
                            buttonText={translate("Search")}
                            type="submit"
                            disabled={amount.length === 0 || invoiceNumber.length === 0 || loadNumber.length === 0}
                            loading={invoiceSearchLoading && !loadInvoiceError && !loadInvoiceFailure}
                        />
                    </Box>
                </form>
                {invoiceSearchLoading && !loadInvoiceError && !loadInvoiceFailure && (
                    <Box><CircularProgress /></Box>
                )}
                {loadInvoiceError && (
                    <Banner severity='negative' title={loadInvoiceError.message}/>
                )}
                <GuestUserRedirect />
            </Stack>
            <Box>
                <Stack>
                    <Typography fontWeight="semiBold" variant='heading300'>{translate("Setup & support")}</Typography>
                    <Box>
                        <Typography variant='body100'>{translate("Want to create an account? Contact")} <a href="mailto:SecurePay@chrobinson.com">SecurePay@chrobinson.com</a> {translate("to get setup today.")}</Typography>
                        <Typography variant='body100'>{translate("If you have questions about how to setup additional electronic payment methods, email")} <a href="mailto:onaccount@chrobinson.com">onaccount@chrobinson.com</a>.</Typography>
                        <Typography variant='body100'>{translate("Have other questions about paying invoices?")} <a href="assets/Navisphere-Secure-Pay-FAQ.pdf" target="_blank">{translate("View our FAQ")}</a>.</Typography>
                    </Box>
                </Stack>
            </Box>
            <Box>
                <Stack>
                    <Typography fontWeight="semiBold" variant='heading300'>{translate("Non-US invoices payment")}</Typography>
                    <Box>
                        <Typography variant="body100">{translate("Email")} <a href="mailto:onaccount@chrobinson.com">onaccount@chrobinson.com</a>  {translate("for ACH set up information")}</Typography>
                        <Typography variant="body100">{translate("Deposit of a check for less than full amount of this invoice shall not constitute an accord and satisfaction, nor a full settlement of this invoice, without prior written agreement.")}</Typography>
                    </Box>
                </Stack>
            </Box>
        </Stack>
    )
}
